import * as React from "react";
import { useEffect, useLayoutEffect, useState } from "react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type {
  ConditionAnalysisBucketRow,
  ConditionAnalysisSituationRow,
} from "../../../../utils/conditionAnalysisSelectors";
import { MarketContextBucketTable } from "./MarketContextBucketTable";
import { MarketContextSituationsTable } from "./MarketContextSituationsTable";
import {
  marketContextActiveBucketsTabPanelSelector,
  marketContextActiveRowSelector,
  marketContextBucketRowSelector,
  marketContextSituationRowSelector,
  scheduleScrollMarketContextRow,
} from "../../../../utils/marketContextEvidenceScroll";

export type MarketContextBucketsTabValue =
  | "best-buckets"
  | "worst-buckets"
  | "best-situations"
  | "worst-situations";

const FULL_TAB_VALUES = [
  "best-buckets",
  "worst-buckets",
  "best-situations",
  "worst-situations",
] as const satisfies readonly MarketContextBucketsTabValue[];

const TAB_LABELS: Record<MarketContextBucketsTabValue, string> = {
  "best-buckets": "Best buckets",
  "worst-buckets": "Worst buckets",
  "best-situations": "Best situations",
  "worst-situations": "Worst situations",
};

/** Brief pulse only — never leave Market in a stuck “locked row” state. */
const EVIDENCE_HIGHLIGHT_CLEAR_MS = 1200;

function isMarketContextBucketsTabValue(
  value: string,
  allowed: readonly MarketContextBucketsTabValue[],
): value is MarketContextBucketsTabValue {
  return (allowed as readonly string[]).includes(value);
}

function restoreBodyPointerEvents(): void {
  if (typeof document === "undefined") return;
  document.body.style.pointerEvents = "";
  // react-remove-scroll can leave these if a modal/select unmounted uncleanly.
  for (const cls of Array.from(document.body.classList)) {
    if (cls.startsWith("block-interactivity-")) {
      document.body.classList.remove(cls);
    }
  }
}

export type MarketContextBucketsSituationsTabsProps = {
  /** Kept for callers; buckets + situations tabs are always shown. */
  mode: "segment" | "full";
  bestBuckets: ConditionAnalysisBucketRow[];
  worstBuckets: ConditionAnalysisBucketRow[];
  bestSituations: ConditionAnalysisSituationRow[];
  worstSituations: ConditionAnalysisSituationRow[];
  metricDefinitions?: Record<string, unknown>;
  onPickBucket?: (row: ConditionAnalysisBucketRow, metricKey: string) => void;
  onPickSituation?: (row: ConditionAnalysisSituationRow) => void;
  onPreviewBucket?: (row: ConditionAnalysisBucketRow) => void;
  onPreviewSituationRow?: (row: ConditionAnalysisSituationRow) => void;
  previewActiveKey?: string | readonly string[];
  previewLoadingKey?: string | readonly string[];
  isBucketPicked?: (row: ConditionAnalysisBucketRow, metricKey: string) => boolean;
  isSituationPicked?: (row: ConditionAnalysisSituationRow) => boolean;
  onPreviewSituation?: (situation: string) => void;
  showManagementPreview?: boolean;
  /** Deep-link from Overview findings (e.g. regime_actionable_filters). */
  focusBucketsTab?: import("../../../../utils/marketContextNavigation").MarketContextBucketsTabValue | null;
  focusBucketsTabNonce?: number;
  highlightSituation?: string | null;
  highlightBucket?: string | null;
  /** Clear parent deep-link after the row is shown (do not leave Market “locked”). */
  onDismissFocus?: () => void;
};

export function MarketContextBucketsSituationsTabs(
  props: MarketContextBucketsSituationsTabsProps,
): React.ReactElement {
  const tabValues = FULL_TAB_VALUES;
  const [tab, setTab] = useState<MarketContextBucketsTabValue>("best-buckets");
  /** Local copy of Overview deep-link — parent focus is cleared so Market is not left locked. */
  const [evidenceFocus, setEvidenceFocus] = useState<{
    nonce: number;
    tab: MarketContextBucketsTabValue;
    situation: string | null;
    bucket: string | null;
  } | null>(null);
  const lastAppliedFocusNonceRef = React.useRef(0);
  const onDismissFocusRef = React.useRef(props.onDismissFocus);
  onDismissFocusRef.current = props.onDismissFocus;

  useEffect(() => {
    setTab("best-buckets");
  }, [props.mode]);

  // Adopt Overview deep-link into local state, then release parent focus.
  useEffect(() => {
    const nonce = props.focusBucketsTabNonce ?? 0;
    const focusTab = props.focusBucketsTab;
    if (!nonce || !focusTab) return;
    if (!isMarketContextBucketsTabValue(focusTab, tabValues)) return;
    if (lastAppliedFocusNonceRef.current === nonce) return;
    lastAppliedFocusNonceRef.current = nonce;

    restoreBodyPointerEvents();
    setEvidenceFocus({
      nonce,
      tab: focusTab,
      situation: props.highlightSituation?.trim() || null,
      bucket: props.highlightBucket?.trim() || null,
    });
    setTab(focusTab);
    // Defer parent clear so this commit can paint the target sub-tab first.
    window.requestAnimationFrame(() => {
      onDismissFocusRef.current?.();
      restoreBodyPointerEvents();
    });
  }, [
    props.focusBucketsTab,
    props.focusBucketsTabNonce,
    props.highlightBucket,
    props.highlightSituation,
    tabValues,
  ]);

  // Pulse briefly, then clear active-row chrome so Market stays fully clickable.
  useEffect(() => {
    if (!evidenceFocus) return;
    restoreBodyPointerEvents();
    // If a Radix modal layer left body.pointerEvents="none" / block-interactivity-*,
    // clicks never reach React handlers — restore on a document capture listener + short poll.
    // Do NOT clear evidence on pointerdown: remounting the row mid-click drops the click.
    const onPointerDown = () => restoreBodyPointerEvents();
    document.addEventListener("pointerdown", onPointerDown, true);
    const poll = window.setInterval(restoreBodyPointerEvents, 100);
    const clearPoll = window.setTimeout(() => window.clearInterval(poll), 2000);
    const clearHighlight = window.setTimeout(() => {
      setEvidenceFocus(null);
      restoreBodyPointerEvents();
    }, EVIDENCE_HIGHLIGHT_CLEAR_MS);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      window.clearInterval(poll);
      window.clearTimeout(clearPoll);
      window.clearTimeout(clearHighlight);
    };
  }, [evidenceFocus]);

  useLayoutEffect(() => {
    if (!evidenceFocus) return;
    if (tab !== evidenceFocus.tab) return;

    const situation = evidenceFocus.situation;
    const bucket = evidenceFocus.bucket;
    const isSituationsTab =
      evidenceFocus.tab === "best-situations" || evidenceFocus.tab === "worst-situations";
    const isBucketsTab =
      evidenceFocus.tab === "best-buckets" || evidenceFocus.tab === "worst-buckets";

    const scopeSelector = marketContextActiveBucketsTabPanelSelector();
    let rowSelector: string | null = null;
    if (isSituationsTab && situation) {
      rowSelector = marketContextSituationRowSelector(situation);
    } else if (isBucketsTab && bucket) {
      rowSelector = marketContextBucketRowSelector(bucket);
    } else if (isSituationsTab || isBucketsTab) {
      rowSelector = marketContextActiveRowSelector();
    }

    if (!rowSelector) return;

    restoreBodyPointerEvents();
    return scheduleScrollMarketContextRow({
      scopeSelector,
      rowSelector,
      maxAttempts: 28,
      attemptMs: 75,
    });
  }, [tab, evidenceFocus]);

  const focusTab = evidenceFocus?.tab ?? null;
  const focusNonce = evidenceFocus?.nonce;
  const localHighlightSituation = evidenceFocus?.situation ?? null;
  const localHighlightBucket = evidenceFocus?.bucket ?? null;

  const onTabChange = (value: string) => {
    if (isMarketContextBucketsTabValue(value, tabValues)) {
      setTab(value);
      // Switching sub-tabs should never leave a stuck evidence lock.
      setEvidenceFocus(null);
      restoreBodyPointerEvents();
    }
  };

  return (
    <Tabs
      data-market-context-buckets-tabs=""
      className="w-full"
      value={tab}
      onValueChange={onTabChange}
      onPointerDownCapture={restoreBodyPointerEvents}
    >
      <TabsList className="min-w-max">
        {tabValues.map((value) => (
          <TabsTrigger key={value} value={value}>
            {TAB_LABELS[value]}
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent
        value="best-buckets"
        className="mt-0 data-[state=inactive]:pointer-events-none data-[state=inactive]:hidden"
      >
        <MarketContextBucketTable
          rows={props.bestBuckets}
          onPickBucket={props.onPickBucket}
          onPreviewBucket={props.onPreviewBucket}
          previewActiveKey={props.previewActiveKey}
          previewLoadingKey={props.previewLoadingKey}
          isPicked={props.isBucketPicked}
          metricDefinitions={props.metricDefinitions}
          highlightBucket={focusTab === "best-buckets" ? localHighlightBucket : null}
          evidenceFocusNonce={focusTab === "best-buckets" ? focusNonce : undefined}
          tableVariant="best"
          emptyMessage="No best buckets available for this view."
        />
      </TabsContent>

      <TabsContent
        value="worst-buckets"
        className="mt-0 data-[state=inactive]:pointer-events-none data-[state=inactive]:hidden"
      >
        <MarketContextBucketTable
          rows={props.worstBuckets}
          onPickBucket={props.onPickBucket}
          onPreviewBucket={props.onPreviewBucket}
          previewActiveKey={props.previewActiveKey}
          previewLoadingKey={props.previewLoadingKey}
          isPicked={props.isBucketPicked}
          metricDefinitions={props.metricDefinitions}
          highlightBucket={focusTab === "worst-buckets" ? localHighlightBucket : null}
          evidenceFocusNonce={focusTab === "worst-buckets" ? focusNonce : undefined}
          tableVariant="worst"
          emptyMessage="No worst buckets available for this view."
        />
      </TabsContent>

      <TabsContent
        value="best-situations"
        className="mt-0 data-[state=inactive]:pointer-events-none data-[state=inactive]:hidden"
      >
        <MarketContextSituationsTable
          rows={props.bestSituations}
          onPickSituation={props.onPickSituation}
          onPreviewSituationRow={props.onPreviewSituationRow}
          previewActiveKey={props.previewActiveKey}
          previewLoadingKey={props.previewLoadingKey}
          isPicked={props.isSituationPicked}
          metricDefinitions={props.metricDefinitions}
          highlightSituation={focusTab === "best-situations" ? localHighlightSituation : null}
          evidenceFocusNonce={focusTab === "best-situations" ? focusNonce : undefined}
          emptyMessage="No best situations available for this view."
        />
      </TabsContent>

      <TabsContent
        value="worst-situations"
        className="mt-0 data-[state=inactive]:pointer-events-none data-[state=inactive]:hidden"
      >
        <MarketContextSituationsTable
          rows={props.worstSituations}
          onPickSituation={props.onPickSituation}
          onPreviewSituationRow={props.onPreviewSituationRow}
          previewActiveKey={props.previewActiveKey}
          previewLoadingKey={props.previewLoadingKey}
          isPicked={props.isSituationPicked}
          metricDefinitions={props.metricDefinitions}
          showManagementPreview={props.showManagementPreview}
          onPreviewSituation={props.onPreviewSituation}
          highlightSituation={focusTab === "worst-situations" ? localHighlightSituation : null}
          evidenceFocusNonce={focusTab === "worst-situations" ? focusNonce : undefined}
          emptyMessage="No worst situations available for this view."
        />
      </TabsContent>
    </Tabs>
  );
}
